import React, { useEffect, useMemo, useRef, useState } from "react";
import api, {
  clearApiGetCache,
  clearAuthTokens,
  fetchAndStoreAccessRouteDecision,
  getAccessRouteBlockedMessage,
  resetAuthRedirectState,
  setAuthTokens,
} from "@/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, EyeOff } from "lucide-react";

const loginStyles = `
.mmj-page { position: relative; min-height: 100vh; width: 100%; overflow: hidden; display: flex; align-items: center; justify-content: center; padding: 32px 16px; background: #022116; }
.mmj-bg { position: absolute; inset: 0; background: radial-gradient(circle at 0% 0%, #047857 0%, transparent 55%), radial-gradient(circle at 100% 0%, #10b981 0%, transparent 55%), radial-gradient(circle at 0% 100%, #065f46 0%, transparent 55%), radial-gradient(circle at 100% 100%, #059669 0%, transparent 55%); background-size: 180% 180%; animation: mmjGradient 28s ease-in-out infinite alternate; opacity: .95; z-index: 0; }
.mmj-blob { position: absolute; width: 420px; height: 420px; border-radius: 60% 40% 55% 45%; background: radial-gradient(circle, rgba(248,241,232,.92), rgba(248,241,232,0)); filter: blur(2px); mix-blend-mode: soft-light; opacity: .42; z-index: 1; }
.mmj-blob-1 { top: -60px; left: -110px; animation: mmjBlob1 22s ease-in-out infinite alternate; }
.mmj-blob-2 { bottom: -120px; right: -130px; animation: mmjBlob2 26s ease-in-out infinite alternate; }
.mmj-blob-3 { top: 40%; left: 55%; width: 340px; height: 340px; animation: mmjBlob3 24s ease-in-out infinite alternate; }
.mmj-wrapper { position: relative; z-index: 3; animation: mmjFloat 14s ease-in-out infinite alternate; width: 100%; display: flex; flex-direction: column; gap: 22px; align-items: center; }
.mmj-wrapper-compact { max-width: 420px; }
.mmj-wrapper-wide { max-width: 840px; }
.mmj-logo { width: 100%; display: flex; flex-direction: column; align-items: center; text-align: center; }
.mmj-logo-img { width: 210px; height: auto; margin-bottom: 12px; filter: drop-shadow(0 10px 24px rgba(0,0,0,.5)); }
.mmj-sub { margin-top: -14px; color: #d1fae5; font-size: 13px; letter-spacing: .32em; text-transform: uppercase; text-align: center; }
.mmj-card { width: 100%; background: rgba(255,255,255,.55); backdrop-filter: blur(26px) saturate(180%); border-radius: 26px; box-shadow: 0 30px 60px rgba(0,0,0,.45), inset 0 0 32px rgba(255,255,255,.35), 0 0 0 1px rgba(255,255,255,.32); padding: 32px 26px 36px; position: relative; animation: mmjCardBreath 13s ease-in-out infinite alternate; }
.mmj-card-compact { max-width: 420px; }
.mmj-card-wide { max-width: 760px; }
.mmj-title { font-size: 28px; font-weight: 500; color: #1f2937; margin-bottom: 24px; }
.mmj-btn { width: 100%; background: #047857 !important; height: 46px; border-radius: 12px; font-size: 16px; }
.mmj-btn:hover { background: #035f41 !important; }
.mmj-link { margin-top: 14px; font-size: 13px; color: #065f46; cursor: pointer; transition: .2s; }
.mmj-link:hover { text-decoration: underline; }
.mmj-top-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.mmj-top-link { font-size: 14px; color: #065f46; cursor: pointer; font-weight: 600; }
.mmj-top-link:hover { text-decoration: underline; }
.mmj-badge { margin-top: 18px; background: rgba(255,255,255,.5); padding: 6px 18px; border-radius: 20px; font-size: 13px; color: #065f46; border: 1px solid rgba(255,255,255,.6); }
.mmj-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.mmj-grid-full { grid-column: 1 / -1; }
.mmj-google-btn { display: flex; align-items: center; justify-content: center; gap: 10px; }
.mmj-google-logo { width: 20px; height: 20px; object-fit: contain; }
.mmj-sep { display: flex; align-items: center; gap: 10px; margin: 2px 0; }
.mmj-sep::before, .mmj-sep::after { content: ""; flex: 1; height: 1px; background: rgba(6, 95, 70, 0.35); }
.mmj-sep span { font-size: 12px; color: #065f46; letter-spacing: 0.06em; text-transform: uppercase; }
.mmj-otp-row { display: flex; gap: 10px; justify-content: center; }
.mmj-otp-cell { width: 42px; height: 46px; text-align: center; font-size: 22px; font-weight: 600; border-radius: 10px; border: 1px solid rgba(6,95,70,.35); background: rgba(255,255,255,.85); }
@media (max-width: 720px) { .mmj-grid { grid-template-columns: 1fr; } .mmj-wrapper-wide { max-width: 420px; } .mmj-card-wide { max-width: 420px; } .mmj-otp-cell { width: 38px; } }
@keyframes mmjGradient { 0% { background-position: 0% 0%; } 50% { background-position: 100% 50%; } 100% { background-position: 40% 80%; } }
@keyframes mmjBlob1 { 0% { border-radius: 60% 40% 55% 45%; transform: translate(0,0); } 100% { border-radius: 40% 60% 40% 60%; transform: translate(28px,40px); } }
@keyframes mmjBlob2 { 0% { border-radius: 55% 45% 60% 40%; transform: translate(0,0); } 100% { border-radius: 45% 55% 40% 60%; transform: translate(-36px,-20px); } }
@keyframes mmjBlob3 { 0% { transform: scale(1) translate(0,0); } 100% { transform: scale(1.06) translate(-20px,30px); } }
@keyframes mmjFloat { 0% { transform: translateY(0px); } 100% { transform: translateY(-12px); } }
@keyframes mmjCardBreath { 0% { transform: scale(1); } 100% { transform: scale(1.015); } }
.mmj-step-bar { display: flex; align-items: center; justify-content: center; gap: 0; margin-bottom: 20px; }
.mmj-step { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.mmj-step-dot { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; border: 2px solid rgba(6,95,70,.3); color: #065f46; background: rgba(255,255,255,.55); flex-shrink: 0; }
.mmj-step-dot.active { background: #047857; color: #fff; border-color: #047857; box-shadow: 0 2px 8px rgba(4,120,87,.35); }
.mmj-step-dot.done { background: #10b981; color: #fff; border-color: #10b981; }
.mmj-step-label { font-size: 10px; color: #065f46; font-weight: 500; white-space: nowrap; text-align: center; }
.mmj-step-connector { width: 36px; height: 2px; background: rgba(6,95,70,.2); margin: 0 2px; margin-bottom: 15px; flex-shrink: 0; }
.mmj-q-grid { display: flex; flex-direction: column; gap: 12px; }
.mmj-q-card { background: rgba(255,255,255,.72); border-radius: 14px; border: 1.5px solid rgba(6,95,70,.18); padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; transition: border-color .2s; }
.mmj-q-card.answered { border-color: #10b981; background: rgba(16,185,129,.06); }
.mmj-q-num { font-size: 11px; font-weight: 700; color: #047857; text-transform: uppercase; letter-spacing: .06em; }
.mmj-q-progress { font-size: 13px; font-weight: 600; color: #047857; text-align: center; margin-bottom: 2px; }
.mmj-q-hint { font-size: 12px; color: #065f46; text-align: center; margin-bottom: 10px; }
.mmj-recovery-card { background: rgba(255,255,255,.72); border-radius: 16px; border: 1.5px solid rgba(6,95,70,.2); padding: 18px; display: flex; flex-direction: column; gap: 12px; }
.mmj-recovery-question { font-size: 14px; font-weight: 500; color: #1f2937; line-height: 1.55; }
.mmj-biz-pill { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,.5); padding: 6px 18px; border-radius: 20px; font-size: 13px; font-family: var(--font-ui, 'Inter', sans-serif); color: #065f46; border: 1px solid rgba(255,255,255,.6); cursor: pointer; transition: background .15s; }
.mmj-biz-pill:hover { background: rgba(255,255,255,.65); }
.mmj-biz-pill-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }
.mmj-biz-picker { position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%); background: rgba(255,255,255,.97); backdrop-filter: blur(22px); border: 1px solid rgba(6,95,70,.18); border-radius: 16px; box-shadow: 0 16px 40px rgba(0,0,0,.22); min-width: 270px; max-width: 320px; z-index: 50; overflow: hidden; font-family: var(--font-ui, 'Inter', sans-serif); }
.mmj-biz-picker-head { padding: 10px 14px 6px; font-size: 10px; font-weight: 700; color: #047857; text-transform: uppercase; letter-spacing: .08em; }
.mmj-biz-picker-item { display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: 9px 14px; text-align: left; border: none; background: transparent; border-bottom: 1px solid rgba(6,95,70,.07); cursor: pointer; transition: background .12s; font-family: var(--font-ui, 'Inter', sans-serif); }
.mmj-biz-picker-item:hover { background: rgba(4,120,87,.06); }
.mmj-biz-picker-item.active { background: rgba(4,120,87,.08); cursor: default; }
.mmj-biz-picker-item.legacy { background: rgba(251,191,36,.04); }
.mmj-biz-picker-item.legacy:hover { background: rgba(251,191,36,.10); }
.mmj-biz-picker-item-name { display: flex; align-items: center; gap: 6px; width: 100%; font-size: 13px; font-weight: 600; color: #1f2937; }
.mmj-biz-picker-item-code { font-size: 10px; color: #6b7280; margin-top: 1px; }
.mmj-biz-picker-item-meta { font-size: 10px; color: #9ca3af; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.mmj-biz-picker-item-warning { font-size: 10px; color: #b45309; margin-top: 3px; line-height: 1.4; }
.mmj-biz-label { display: inline-flex; align-items: center; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; flex-shrink: 0; }
.mmj-biz-label.canonical { background: rgba(4,120,87,.12); color: #047857; }
.mmj-biz-label.legacy_clone { background: rgba(217,119,6,.13); color: #b45309; }
.mmj-biz-label.incomplete { background: rgba(107,114,128,.12); color: #6b7280; }
.mmj-biz-picker-add { display: flex; align-items: center; gap: 8px; width: 100%; padding: 10px 14px; font-size: 13px; font-weight: 600; color: #047857; border: none; background: transparent; cursor: pointer; transition: background .12s; font-family: var(--font-ui, 'Inter', sans-serif); }
.mmj-biz-picker-add:hover { background: rgba(4,120,87,.06); }
.mmj-biz-modal-overlay { position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,.48); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; }
.mmj-biz-modal { background: rgba(255,255,255,.97); border-radius: 20px; padding: 28px 24px; max-width: 380px; width: 100%; box-shadow: 0 24px 48px rgba(0,0,0,.3); font-family: var(--font-ui, 'Inter', sans-serif); }
`;

function PasswordInput({ value, onChange, placeholder, id, name, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="pr-10"
      />
      <div
        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer select-none"
        onMouseDown={() => setShow(true)}
        onMouseUp={() => setShow(false)}
        onMouseLeave={() => setShow(false)}
        onTouchStart={() => setShow(true)}
        onTouchEnd={() => setShow(false)}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </div>
    </div>
  );
}

function OtpInputs({ value, onChange }) {
  const chars = (value || "").padEnd(6, " ").slice(0, 6).split("");

  const updateAt = (index, next) => {
    const digit = String(next || "").replace(/\D/g, "").slice(-1);
    const arr = (value || "").split("").slice(0, 6);
    arr[index] = digit;
    onChange(arr.join("").slice(0, 6));

    if (digit && index < 5) {
      const nextEl = document.getElementById(`signup-otp-${index + 1}`);
      if (nextEl) nextEl.focus();
    }
  };

  return (
    <div className="mmj-otp-row">
      {chars.map((ch, idx) => (
        <input
          key={idx}
          id={`signup-otp-${idx}`}
          name={`signup_otp_${idx}`}
          className="mmj-otp-cell"
          value={ch.trim()}
          onChange={(e) => updateAt(idx, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !chars[idx].trim() && idx > 0) {
              const prev = document.getElementById(`signup-otp-${idx - 1}`);
              if (prev) prev.focus();
            }
          }}
          maxLength={1}
          inputMode="numeric"
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

function splitFullName(fullName = "") {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" ") || "",
  };
}

const SECURITY_QUESTIONS_POOL = [
  "What was the name of your first shop, business, or venture?",
  "What was the nickname your family called you growing up?",
  "What was the first area or locality where you started doing business?",
  "What is the name of a teacher, mentor, or elder who influenced your career?",
  "What was the name of your first employee, apprentice, or helper?",
  "What was the name of the first jewellery supplier or company you dealt with?",
  "What was the first city where you attended a trade exhibition or business event?",
  "What was the name of the street or locality where you spent most of your childhood?",
  "What was the first vehicle registration number or nickname of a vehicle your family used regularly?",
  "What is a word or phrase that has personal significance to you but is not publicly known?",
];

export default function Login({ setUser }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const navigate = useNavigate();
  const googlePrefillPopupRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    otp: "",
    newPassword: "",
  });
  const [resetFlow, setResetFlow] = useState({
    email: "",
    passcode: "",
    step: 0,
    businessName: "",
    backupMethod: "",
    lastBackupAt: "",
    backupAgeDays: null,
    hasRecoveryQuestion: false,
    recoveryQuestion: "",
    recoveryAnswer: "",
    vaultPassword: "",
    vaultVerified: false,
    otp: "",
    otpSent: false,
    otpCooldown: 0,
    typedConfirmation: "",
    confirmInfo: null,
    executing: false,
    progress: 0,
    progressLabel: "",
  });
  // Keep for backward compat with any remaining references
  const clearDataFlow = resetFlow;
  const setClearDataFlow = (updater) => {
    setResetFlow((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return { ...prev, ...next };
    });
  };
  const [clearDataOtpSent, setClearDataOtpSent] = useState(false);
  const [clearDataOtpCooldown, setClearDataOtpCooldown] = useState(0);
  const [clearDataConfirmInfo, setClearDataConfirmInfo] = useState(null);
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [takeoverSessions, setTakeoverSessions] = useState([]);
  const [takeoverSelected, setTakeoverSelected] = useState([]);
  const [takeoverOtpSent, setTakeoverOtpSent] = useState(false);
  const [takeoverOtp, setTakeoverOtp] = useState("");
  const [deviceLimitOpen, setDeviceLimitOpen] = useState(false);
  const [deviceLimitDevices, setDeviceLimitDevices] = useState([]);
  const [deviceLimitSelected, setDeviceLimitSelected] = useState([]);
  const [deviceLimitOtpSent, setDeviceLimitOtpSent] = useState(false);
  const [deviceLimitOtp, setDeviceLimitOtp] = useState("");
  const [newDeviceOtpOpen, setNewDeviceOtpOpen] = useState(false);
  const [newDeviceOtp, setNewDeviceOtp] = useState("");
  const [staffRegisterLockedCode, setStaffRegisterLockedCode] = useState("");

  const [signup, setSignup] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    business_address: "",
    city: "",
    state: "",
    gst_number: "",
    mobile_number: "",
    landline_number: "",
    registered_email: "",
    login_password: "",
    confirm_login_password: "",
    vault_password: "",
    confirm_vault_password: "",
    otp: "",
    google_prefill_full_name: "",
    google_profile_image_url: "",
  });
  const [staffRegister, setStaffRegister] = useState({
    full_name: "",
    email: "",
    password: "",
    business_code: "",
  });
  const [secQSetup, setSecQSetup] = useState(
    Array.from({ length: 5 }, () => ({ question: "", answer: "" }))
  );
  const [recoveryQuestion, setRecoveryQuestion] = useState("");
  const [recoveryAnswer, setRecoveryAnswer] = useState("");
  const isSignupFlow = mode === "register" || mode === "signupOtp" || mode === "signupVault" || mode === "signupSecurityQuestions";

  const tenantId = useMemo(() => {
    const raw = `${signup.business_name}-${signup.mobile_number}`.toLowerCase();
    return raw.replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "default";
  }, [signup.business_name, signup.mobile_number]);

  const handle = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const handleSignup = (key) => (e) => setSignup((prev) => ({ ...prev, [key]: e.target.value }));
  const handleStaffRegister = (key) => (e) => setStaffRegister((prev) => ({ ...prev, [key]: e.target.value }));

  const [businesses, setBusinesses] = useState([]);
  const [activeBusinessId, setActiveBusinessId] = useState("");
  const [businessPickerOpen, setBusinessPickerOpen] = useState(false);
  const [switchConfirmBusiness, setSwitchConfirmBusiness] = useState(null);
  const [switchVaultDialog, setSwitchVaultDialog] = useState(null);
  const [switchVaultInput, setSwitchVaultInput] = useState("");
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [addExistingStep, setAddExistingStep] = useState("inspect");
  const [addExistingFile, setAddExistingFile] = useState(null);
  const [addExistingTransferCode, setAddExistingTransferCode] = useState("");
  const [addExistingInspectResult, setAddExistingInspectResult] = useState(null);
  const [addExistingInspecting, setAddExistingInspecting] = useState(false);
  const [addExistingSessionId, setAddExistingSessionId] = useState("");
  const [addExistingRecoveryQuestion, setAddExistingRecoveryQuestion] = useState({ required: false, question: "" });
  const [addExistingVaultPassword, setAddExistingVaultPassword] = useState("");
  const [addExistingSecurityAnswer, setAddExistingSecurityAnswer] = useState("");
  const [addExistingRestoring, setAddExistingRestoring] = useState(false);
  const [addExistingRestoreResult, setAddExistingRestoreResult] = useState(null);
  const [addExistingFinalizing, setAddExistingFinalizing] = useState(false);
  const [addExistingFinalizeResult, setAddExistingFinalizeResult] = useState(null);
  const [addExistingReplaceLocalCopy, setAddExistingReplaceLocalCopy] = useState(false);
  const businessPickerRef = useRef(null);

  const activeBusinessName = useMemo(() => {
    const match = businesses.find((b) => b.business_id === activeBusinessId);
    return match?.business_name || "";
  }, [businesses, activeBusinessId]);

  useEffect(() => {
    api.get("/local-security/businesses", { skipDedupe: true })
      .then((res) => {
        setBusinesses(res.data?.businesses || []);
        setActiveBusinessId(String(res.data?.active_business_id || ""));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!businessPickerOpen) return;
    const onDown = (e) => {
      if (businessPickerRef.current && !businessPickerRef.current.contains(e.target)) {
        setBusinessPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [businessPickerOpen]);

  const switchBusiness = async (businessId, vaultPassword = "") => {
    if (!businessId || businessId === activeBusinessId) {
      setBusinessPickerOpen(false);
      return;
    }
    const target = businesses.find((b) => b.business_id === businessId);

    // No vault password yet — show warning dialog first for non-canonical, then vault dialog
    if (!vaultPassword) {
      if (target?.label && target.label !== "canonical") {
        setBusinessPickerOpen(false);
        setSwitchConfirmBusiness(target);
        return;
      }
      // Canonical: go straight to vault dialog
      setBusinessPickerOpen(false);
      setSwitchVaultDialog(target || { business_id: businessId, business_name: businessId });
      setSwitchVaultInput("");
      return;
    }

    // Vault password provided — attempt the authenticated switch
    setSwitchVaultDialog(null);
    setSwitchVaultInput("");
    setSwitchConfirmBusiness(null);
    setLoading(true);
    try {
      const res = await api.post("/local-security/businesses/pre-login-select", {
        business_id: businessId,
        vault_password: vaultPassword,
      });
      clearAuthTokens();
      clearApiGetCache();
      const newBid = String(res.data?.active_business_id || businessId);
      try { localStorage.setItem("jewelflow_active_business_id", newBid); } catch { /* ignore */ }
      try { localStorage.removeItem("mmj_active_business_id"); } catch { /* ignore */ }
      setActiveBusinessId(newBid);
      setBusinessPickerOpen(false);
      const name = businesses.find((b) => b.business_id === businessId)?.business_name || businessId;
      toast.success(`Switched to ${name}`);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(err?.response?.data?.detail || "Business switch failed");
      if (status === 401) {
        // Wrong vault password — re-open vault dialog for retry
        setSwitchVaultDialog(target || { business_id: businessId, business_name: businessId });
        setSwitchVaultInput("");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAddExistingState = () => {
    setAddExistingStep("inspect");
    setAddExistingFile(null);
    setAddExistingTransferCode("");
    setAddExistingInspectResult(null);
    setAddExistingInspecting(false);
    setAddExistingSessionId("");
    setAddExistingRecoveryQuestion({ required: false, question: "" });
    setAddExistingVaultPassword("");
    setAddExistingSecurityAnswer("");
    setAddExistingRestoring(false);
    setAddExistingRestoreResult(null);
    setAddExistingFinalizing(false);
    setAddExistingFinalizeResult(null);
    setAddExistingReplaceLocalCopy(false);
  };

  const closeAddExistingModal = () => {
    setAddExistingOpen(false);
    resetAddExistingState();
  };

  const inspectAddExistingPackage = async () => {
    if (!addExistingFile) {
      toast.error("Choose a transfer package first");
      return;
    }
    setAddExistingInspecting(true);
    setAddExistingInspectResult(null);
    try {
      const formData = new FormData();
      formData.append("package_file", addExistingFile);
      if (String(addExistingTransferCode || "").trim()) {
        formData.append("transfer_code", String(addExistingTransferCode || "").trim());
      }
      const res = await api.post("/local-security/transfer-package/inspect", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = res.data || null;
      setAddExistingInspectResult(next);
      setAddExistingSessionId(String(next?.session_id || ""));
      setAddExistingRecoveryQuestion(next?.recovery_question || { required: false, question: "" });
      setAddExistingRestoreResult(null);
      setAddExistingFinalizeResult(null);
      setAddExistingReplaceLocalCopy(false);
      if (next?.verification?.transfer_code_verified) {
        setAddExistingStep("verified");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Package inspection failed");
    } finally {
      setAddExistingInspecting(false);
    }
  };

  const stageAddExistingRestore = async () => {
    if (!addExistingSessionId) {
      toast.error("Inspect the transfer package again");
      return;
    }
    if (!addExistingVaultPassword.trim()) {
      toast.error("Enter vault password");
      return;
    }
    if (addExistingRecoveryQuestion?.required && !addExistingSecurityAnswer.trim()) {
      toast.error("Answer the security question");
      return;
    }
    setAddExistingRestoring(true);
    setAddExistingRestoreResult(null);
    try {
      const res = await api.post("/local-security/transfer-package/restore-stage", {
        session_id: addExistingSessionId,
        vault_password: addExistingVaultPassword,
        security_answer: addExistingSecurityAnswer,
      });
      setAddExistingRestoreResult(res.data || null);
      setAddExistingStep("report");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.validation_report) {
        setAddExistingRestoreResult(detail);
        setAddExistingStep("report");
      }
      toast.error(
        detail?.message ||
        (typeof detail === "string" ? detail : "") ||
        "Restore validation failed"
      );
    } finally {
      setAddExistingRestoring(false);
    }
  };

  const finalizeAddExistingRestore = async () => {
    if (!addExistingSessionId) {
      toast.error("Restore session expired. Inspect the package again.");
      return;
    }
    setAddExistingFinalizing(true);
    try {
      const res = await api.post("/local-security/transfer-package/finalize", {
        session_id: addExistingSessionId,
        confirm_primary_transfer: true,
        replace_local_copy: addExistingReplaceLocalCopy,
      });
      const next = res.data || null;
      setAddExistingFinalizeResult(next);
      setAddExistingStep("done");
      toast.success(next?.message || "Business moved successfully.");
      try {
        const bid = String(next?.active_business_id || "");
        if (bid) {
          localStorage.setItem("jewelflow_active_business_id", bid);
          localStorage.removeItem("mmj_active_business_id");
        }
      } catch {
        // ignore storage failures
      }
      if (window.JEWEL_FLOW_ENV?.isElectron && window.electronAPI) {
        try {
          if (next?.active_business_id) {
            await window.electronAPI.setActiveBusinessSlug(String(next.active_business_id));
          }
          await window.electronAPI.ensureBackgroundServices();
        } catch {
          // keep success flow even if startup handoff needs manual reload
        }
      }
      window.setTimeout(() => window.location.reload(), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.reason === "replace_confirmation_required") {
        setAddExistingRestoreResult((prev) => ({
          ...(prev || {}),
          business_conflict: detail.business_conflict,
          message: detail.message,
        }));
      }
      toast.error(
        detail?.message ||
        (typeof detail === "string" ? detail : "") ||
        "Final activation failed"
      );
    } finally {
      setAddExistingFinalizing(false);
    }
  };

  const parseLoginLaunchParams = () => {
    const hash = String(window.location.hash || "");
    const [, query = ""] = hash.split("?");
    const params = new URLSearchParams(query);
    return {
      mode: String(params.get("mode") || "").trim().toLowerCase(),
      businessCode: String(params.get("business_code") || "").trim(),
    };
  };

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  useEffect(() => {
    if (clearDataOtpCooldown <= 0) return;
    const t = setTimeout(() => setClearDataOtpCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [clearDataOtpCooldown]);

  useEffect(() => {
    if ((resetFlow.otpCooldown || 0) <= 0) return;
    const t = setTimeout(() => setResetFlow((prev) => ({ ...prev, otpCooldown: (prev.otpCooldown || 0) - 1 })), 1000);
    return () => clearTimeout(t);
  }, [resetFlow.otpCooldown]);

  useEffect(() => {
    api.get("/health").catch(() => {
      // Availability logs are shown in devtools through axios errors.
    });
  }, []);

  const ensureDesktopBackgroundServices = async () => {
    try {
      if (typeof window !== "undefined" && window.electronAPI?.ensureBackgroundServices) {
        await window.electronAPI.ensureBackgroundServices();
      }
    } catch {
      // Non-fatal: login should still proceed in browser-only contexts.
    }
  };

  const finalizeLogin = async (authData, successMessage = "Login successful") => {
    if (!authData?.access_token) return;
    clearApiGetCache();
    resetAuthRedirectState();
    setAuthTokens({
      access_token: authData.access_token,
      refresh_token: authData.refresh_token,
      session_id: authData.session_id,
      user: authData.user,
    });
    try {
      localStorage.setItem("mmj_last_activity_at", String(Date.now()));
      localStorage.removeItem("mmj_session_timeout_debug");
      localStorage.removeItem("mmj_clear_mode_session");
    } catch {
      // ignore storage write errors
    }
    localStorage.setItem("jewelflow_active_business_id", String(authData?.user?.business_id || tenantId || ""));
    if (setUser) setUser(authData.user);
    try {
      const accessDecision = await fetchAndStoreAccessRouteDecision({ suppressLogoutOnUnauthorized: true });
      if (accessDecision?.blocked) {
        clearAuthTokens();
        if (setUser) setUser(null);
        toast.error(getAccessRouteBlockedMessage(accessDecision));
        return;
      }
    } catch {
      // Keep current login flow intact if the advisory access check is temporarily unavailable.
    }
    await ensureDesktopBackgroundServices();
    setNewDeviceOtpOpen(false);
    setNewDeviceOtp("");
    setTakeoverOpen(false);
    setDeviceLimitOpen(false);
    toast.success(successMessage);
    navigate("/", { replace: true });
  };

  useEffect(() => {
    document.title = "JewelFlow | Crafted for Efficiency.";
  }, []);

  useEffect(() => {
    const applyLaunchParams = () => {
      const launch = parseLoginLaunchParams();
      const isStaffMode = launch.mode === "staff-register";
      if (!isStaffMode) return;
      setMode("staffRegister");
      setStaffRegisterLockedCode(launch.businessCode);
      setStaffRegister((prev) => ({
        ...prev,
        business_code: launch.businessCode || prev.business_code,
      }));
    };
    applyLaunchParams();
    window.addEventListener("hashchange", applyLaunchParams);
    return () => window.removeEventListener("hashchange", applyLaunchParams);
  }, []);

  useEffect(() => {
    const onMessage = async (event) => {
      const payload = event?.data;
      if (!payload || payload.type !== "jewelflow_google_prefill") return;

      const apiOrigin = (() => {
        try {
          return new URL(String(api.defaults.baseURL || ""), window.location.origin).origin;
        } catch {
          return "";
        }
      })();
      const eventOrigin = String(event.origin || "").trim();
      if (apiOrigin && eventOrigin && eventOrigin !== apiOrigin) return;

      try {
        if (googlePrefillPopupRef.current && !googlePrefillPopupRef.current.closed) {
          googlePrefillPopupRef.current.close();
        }
      } catch {
        // ignore popup close failures
      }
      googlePrefillPopupRef.current = null;

      if (!payload.ok) {
        toast.error(payload.error || "Google autofill failed");
        return;
      }

      const signupPrefillToken = String(payload.signup_prefill_token || "").trim();
      if (!signupPrefillToken) {
        toast.error("Google autofill token was missing");
        return;
      }

      setLoading(true);
      try {
        const res = await api.post("/auth/google/prefill/callback", {
          signup_prefill_token: signupPrefillToken,
        });
        const fullName = String(res?.data?.full_name || "").trim();
        const email = String(res?.data?.email || "").trim().toLowerCase();
        const pictureUrl = String(res?.data?.picture_url || "").trim();
        if (!email) {
          toast.error("Google account email was not returned.");
          return;
        }
        const parts = splitFullName(fullName);
        setSignup((prev) => ({
          ...prev,
          registered_email: email,
          first_name: parts.first_name || prev.first_name,
          last_name: parts.last_name || prev.last_name,
          google_prefill_full_name: fullName,
          google_profile_image_url: pictureUrl,
        }));
        toast.success("Signup fields auto-filled from Google");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to apply Google autofill");
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const loginNow = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Enter both fields");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/login", {
        email: form.email,
        password: form.password,
        otp: newDeviceOtpOpen ? String(newDeviceOtp || "").trim() : "",
      });

      if (res.data?.access_token) {
        await finalizeLogin(res.data, "Login successful");
      }
    } catch (err) {
      if (err?.response?.status === 428 && err?.response?.data?.detail?.code === "new_device_otp_required") {
        setNewDeviceOtpOpen(true);
        setNewDeviceOtp("");
        toast.info(err?.response?.data?.detail?.message || "New device â€” enter the OTP sent to your email");
        return;
      }
      if (err?.response?.status === 428 && err?.response?.data?.detail?.code === "clear_data_challenge_required") {
        const resetEmail = String(form.email || "").trim().toLowerCase();
        setResetFlow({
          email: resetEmail,
          passcode: String(form.password || ""),
          step: 0,
          businessName: "",
          backupMethod: "",
          lastBackupAt: "",
          backupAgeDays: null,
          hasRecoveryQuestion: false,
          recoveryQuestion: "",
          recoveryAnswer: "",
          vaultPassword: "",
          vaultVerified: false,
          otp: "",
          otpSent: false,
          otpCooldown: 0,
          typedConfirmation: "",
          confirmInfo: null,
          executing: false,
          progress: 0,
          progressLabel: "",
        });
        setClearDataOtpSent(false);
        setClearDataOtpCooldown(0);
        setClearDataConfirmInfo(null);
        setMode("businessReset");
        _loadResetFlowInfo(resetEmail);
        return;
      }
      if (err?.response?.status === 423) {
        navigate("/vault-unlock?mode=general", { replace: true });
        return;
      }
      if (err?.response?.status === 409 && err?.response?.data?.detail?.code === "concurrent_session_limit") {
        const sessions = Array.isArray(err?.response?.data?.detail?.sessions) ? err.response.data.detail.sessions : [];
        setTakeoverSessions(sessions);
        setTakeoverSelected(sessions.map((s) => String(s.id || "")));
        setTakeoverOtp("");
        setTakeoverOtpSent(false);
        setTakeoverOpen(true);
        toast.error(err?.response?.data?.detail?.message || "Maximum active sessions reached");
        return;
      }
      if (err?.response?.status === 409 && err?.response?.data?.detail?.code === "device_limit_reached") {
        const devices = Array.isArray(err?.response?.data?.detail?.devices) ? err.response.data.detail.devices : [];
        setDeviceLimitDevices(devices);
        setDeviceLimitSelected(devices.map((d) => String(d.id || "")));
        setDeviceLimitOtp("");
        setDeviceLimitOtpSent(false);
        setDeviceLimitOpen(true);
        toast.error(err?.response?.data?.detail?.message || "Maximum active devices reached");
        return;
      }
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const requestDeviceLimitOtp = async () => {
    if (!form.email || !form.password) {
      toast.error("Enter email and password");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/login/device-limit/request-otp", {
        email: form.email,
        password: form.password,
      });
      setDeviceLimitOtpSent(true);
      toast.success("OTP sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const confirmDeviceLimit = async () => {
    if (!deviceLimitOtpSent) {
      await requestDeviceLimitOtp();
      return;
    }
    if (!deviceLimitOtp || !deviceLimitSelected.length) {
      toast.error("Select device(s) and enter OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login/device-limit/confirm", {
        email: form.email,
        password: form.password,
        otp: deviceLimitOtp,
        device_ids: deviceLimitSelected,
      });
      if (res.data?.access_token) {
        await finalizeLogin(res.data, "Logged in on this device");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Device switch failed");
    } finally {
      setLoading(false);
    }
  };

  const requestTakeoverOtp = async () => {
    if (!form.email || !form.password) {
      toast.error("Enter email and password");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login/takeover/request-otp", {
        email: form.email,
        password: form.password,
      });
      const sessions = Array.isArray(res?.data?.sessions) ? res.data.sessions : takeoverSessions;
      setTakeoverSessions(sessions);
      if (!takeoverSelected.length) setTakeoverSelected(sessions.map((s) => String(s.id || "")));
      setTakeoverOtpSent(true);
      toast.success("OTP sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const confirmTakeover = async () => {
    if (!takeoverOtpSent) {
      await requestTakeoverOtp();
      return;
    }
    if (!takeoverOtp || !takeoverSelected.length) {
      toast.error("Select session(s) and enter OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login/takeover/confirm", {
        email: form.email,
        password: form.password,
        otp: takeoverOtp,
        session_ids: takeoverSelected,
      });
      if (res.data?.access_token) {
        await finalizeLogin(res.data, "Logged in on this device");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Takeover failed");
    } finally {
      setLoading(false);
    }
  };

  const requestClearDataOtp = async () => {
    if (!clearDataFlow.email) return toast.error("Enter owner email");
    setLoading(true);
    try {
      await api.post("/local-security/clear-data/request-otp", { email: clearDataFlow.email });
      setClearDataOtpSent(true);
      setClearDataOtpCooldown(60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const confirmClearData = async (e, confirmedBusinessId = "") => {
    if (e && e.preventDefault) e.preventDefault();
    if (!clearDataOtpSent) {
      await requestClearDataOtp();
      return;
    }
    if (!clearDataFlow.otp || !clearDataFlow.vault_password) {
      toast.error("Enter OTP and vault password");
      return;
    }
    setLoading(true);
    try {
      await api.post("/local-security/clear-data/execute", {
        email: clearDataFlow.email,
        clear_data_passcode: clearDataFlow.passcode,
        otp: clearDataFlow.otp,
        vault_password: clearDataFlow.vault_password,
        confirm_business_id: confirmedBusinessId,
      });
      clearAuthTokens();
      setClearDataConfirmInfo(null);
      localStorage.setItem("mmj_clear_mode_session", "1");
      setForm((prev) => ({ ...prev, password: "" }));
      setClearDataOtpSent(false);
      setClearDataOtpCooldown(0);
      setMode("login");
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.detail?.reason === "business_confirmation_required") {
        setClearDataConfirmInfo(err.response.data.detail);
        return;
      }
      toast.error(err?.response?.data?.detail || "Clear data failed");
    } finally {
      setLoading(false);
    }
  };

  const _loadResetFlowInfo = async (email) => {
    try {
      const [statusRes, backupsRes] = await Promise.allSettled([
        api.get("/local-security/status"),
        api.get("/local-security/backups"),
      ]);
      const status = statusRes.status === "fulfilled" ? statusRes.value.data : {};
      const backups = backupsRes.status === "fulfilled" ? backupsRes.value.data?.backups || [] : [];
      const autoCfg = status.backup_auto || {};
      const dest = String(autoCfg.destination || "local").toLowerCase();
      const backupMethod = dest === "both" ? "Cloud and Manual Backup" : dest === "cloud" ? "Cloud Backup" : "Manual Backup";
      let lastBackupAt = String(status.last_auto_backup_at || "");
      if (!lastBackupAt && backups.length > 0) lastBackupAt = String(backups[0]?.modified_at || "");
      let backupAgeDays = null;
      if (lastBackupAt) {
        const msAgo = Date.now() - new Date(lastBackupAt).getTime();
        backupAgeDays = Math.floor(msAgo / 86400000);
      }
      let hasRecoveryQuestion = false;
      let recoveryQuestion = "";
      try {
        const qRes = await api.post("/auth/forgot-password/get-question", { email });
        recoveryQuestion = String(qRes.data?.question || "");
        hasRecoveryQuestion = !!recoveryQuestion;
      } catch {
        hasRecoveryQuestion = false;
      }
      setResetFlow((prev) => ({
        ...prev,
        businessName: String(status.business_name || ""),
        backupMethod,
        lastBackupAt,
        backupAgeDays,
        hasRecoveryQuestion,
        recoveryQuestion,
      }));
    } catch {
      // Non-fatal; user can still proceed
    }
  };

  const resetStepVerifyVault = async () => {
    if (!resetFlow.vaultPassword) { toast.error("Enter your vault password"); return; }
    setLoading(true);
    try {
      await api.post("/local-security/reset-business/verify-vault", { vault_password: resetFlow.vaultPassword });
      setResetFlow((prev) => ({ ...prev, vaultVerified: true, step: prev.hasRecoveryQuestion ? 2 : 3 }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vault password incorrect");
    } finally {
      setLoading(false);
    }
  };

  const resetStepVerifyRecovery = async () => {
    if (!resetFlow.recoveryAnswer.trim()) { toast.error("Enter your answer"); return; }
    setLoading(true);
    try {
      await api.post("/local-security/reset-business/verify-recovery", {
        email: resetFlow.email,
        answer: resetFlow.recoveryAnswer.trim(),
      });
      setResetFlow((prev) => ({ ...prev, step: 3 }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect answer");
    } finally {
      setLoading(false);
    }
  };

  const resetStepRequestOtp = async () => {
    setLoading(true);
    try {
      await api.post("/local-security/reset-business/request-otp", { email: resetFlow.email });
      setResetFlow((prev) => ({ ...prev, otpSent: true, otpCooldown: 60 }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const resetStepExecute = async (confirmedBusinessId = "") => {
    const typed = resetFlow.typedConfirmation.trim();
    if (!typed) { toast.error("Type DELETE BUSINESS or your business name to confirm"); return; }
    if (!resetFlow.otp || String(resetFlow.otp).length < 6) { toast.error("Enter the 6-digit OTP"); return; }

    setLoading(true);
    setResetFlow((prev) => ({ ...prev, executing: true, progress: 5, progressLabel: "Preparing secure reset..." }));

    const progressSteps = [
      { pct: 12, label: "Preparing secure reset..." },
      { pct: 34, label: "Verifying authorization..." },
      { pct: 57, label: "Updating workspace..." },
      { pct: 81, label: "Finalizing..." },
      { pct: 95, label: "Opening business..." },
    ];
    let stepIdx = 0;
    let progressInterval = setInterval(() => {
      if (stepIdx < progressSteps.length - 1) {
        stepIdx++;
        setResetFlow((prev) => ({
          ...prev,
          progress: progressSteps[stepIdx].pct,
          progressLabel: progressSteps[stepIdx].label,
        }));
      }
    }, 800);

    // Clears the interval from every exit path — prevents leaked timers
    // when Phase A (409) fires and a second interval would otherwise start.
    const stopProgress = () => {
      clearInterval(progressInterval);
    };

    // Once the execute endpoint returns 200, the DB has committed.
    // Nothing that happens after (auto-login, background services, navigation)
    // should ever show "Reset failed". This flag enforces that guarantee.
    let resetSucceeded = false;

    try {
      await api.post("/local-security/reset-business/execute", {
        email: resetFlow.email,
        reset_key: resetFlow.passcode,
        vault_password: resetFlow.vaultPassword,
        otp: resetFlow.otp,
        typed_confirmation: typed,
        confirm_business_id: confirmedBusinessId,
      });

      // ── Reset committed — everything below is best-effort ──────────────
      resetSucceeded = true;
      stopProgress();
      setResetFlow((prev) => ({ ...prev, progress: 100, progressLabel: "Opening business..." }));
      clearAuthTokens();
      await new Promise((r) => setTimeout(r, 400));

      // Auto-login with vault password. If the login password differs from
      // the vault password, or any other issue arises, fall back to the
      // login screen silently — the reset has already committed.
      let autoLoggedIn = false;
      try {
        const loginRes = await api.post("/auth/login", {
          email: resetFlow.email,
          password: resetFlow.vaultPassword,
          otp: "",
        });
        if (loginRes.data?.access_token) {
          await finalizeLogin(loginRes.data, "Business opened");
          autoLoggedIn = true;
        }
      } catch {
        // Auto-login failed — fall through to login screen
      }

      localStorage.removeItem("mmj_clear_mode_session");
      if (!autoLoggedIn) {
        setMode("login");
      }

    } catch (err) {
      stopProgress();

      // If the DB committed but something after it threw, navigate home anyway.
      if (resetSucceeded) {
        navigate("/", { replace: true });
        return;
      }

      // Phase A — server needs business confirmation; no deletion has started.
      if (err?.response?.status === 409 && err?.response?.data?.detail?.reason === "business_confirmation_required") {
        setResetFlow((prev) => ({
          ...prev,
          executing: false,
          progress: 0,
          progressLabel: "",
          confirmInfo: err.response.data.detail,
        }));
        return;
      }

      // Any other error — the reset did NOT happen.
      setResetFlow((prev) => ({ ...prev, executing: false, progress: 0, progressLabel: "" }));
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Check your entries and try again.");

    } finally {
      setLoading(false);
    }
  };

  const requestForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Enter email");
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password/get-question", { email: form.email });
      setRecoveryQuestion(res.data.question || "");
      setRecoveryAnswer("");
      setMode("securityQuestion");
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "");
      if (detail.includes("No security questions")) {
        // Account exists but has no security questions â€” fall back to direct OTP
        try {
          await api.post("/auth/request-password-reset", { email: form.email });
          toast.success("OTP sent to email");
          setMode("otp");
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Failed to send OTP");
        }
      } else {
        toast.error(detail || "Failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyRecoveryQuestion = async (e) => {
    e.preventDefault();
    if (!recoveryAnswer.trim()) return toast.error("Enter your answer");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify-question", {
        email: form.email,
        answer: recoveryAnswer,
      });
      toast.success("Verified. OTP sent to your email.");
      setMode("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSecQChange = (idx, field, value) => {
    setSecQSetup((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const submitSecurityQuestionsSetup = async (e) => {
    e.preventDefault();
    const resolved = secQSetup.map((item) => ({
      question: item.question === "__custom__" ? (item._customQ || "").trim() : item.question,
      answer: item.answer,
    }));
    for (let i = 0; i < resolved.length; i++) {
      if (!resolved[i].question) return toast.error(`Select or write a question for #${i + 1}`);
      if (!resolved[i].answer.trim()) return toast.error(`Enter answer for question #${i + 1}`);
    }
    const qs = resolved.map((q) => q.question);
    if (new Set(qs).size !== qs.length) return toast.error("Each question must be unique");
    setLoading(true);
    try {
      await api.post("/auth/setup-security-questions-initial", {
        email: signup.registered_email,
        questions: resolved,
      });
      toast.success("Security questions saved. Please sign in.");
      setMode("login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save security questions");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpReset = async (e) => {
    e.preventDefault();
    if (!form.email || !form.otp || !form.newPassword) return toast.error("Fill all fields");
    setLoading(true);
    try {
      await api.post("/auth/verify-password-reset", {
        email: form.email,
        otp: form.otp,
        new_password: form.newPassword,
      });
      toast.success("Password updated");
      setMode("login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const submitSignupDetails = async (e) => {
    e.preventDefault();

    const required = [
      "first_name",
      "last_name",
      "business_name",
      "business_address",
      "city",
      "state",
      "gst_number",
      "mobile_number",
      "registered_email",
      "login_password",
      "confirm_login_password",
    ];
    for (const key of required) {
      if (!String(signup[key] || "").trim()) {
        toast.error("Please fill all required signup fields");
        return;
      }
    }

    if (signup.login_password !== signup.confirm_login_password) {
      toast.error("Login passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const selectRes = await api.post("/local-security/businesses/select", { business_id: tenantId });
      localStorage.setItem("jewelflow_active_business_id", String(selectRes?.data?.active_business_id || tenantId));
      await api.post("/local-security/setup/request-otp", {
        email: signup.registered_email,
        mobile_number: signup.mobile_number,
        gst_number: signup.gst_number,
      });
      setOtpCooldown(60);
      setMode("signupOtp");
      toast.success("OTP sent to registered email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start signup");
    } finally {
      setLoading(false);
    }
  };

  const autofillFromGoogle = () => {
    const apiBase = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    if (!apiBase) {
      toast.error("Backend URL is unavailable for Google autofill.");
      return;
    }
    const currentOrigin = (() => {
      const origin = String(window.location.origin || "").trim();
      return origin && origin !== "null" ? origin : "";
    })();
    const startUrl = `${apiBase}/auth/google/prefill/start${currentOrigin ? `?origin=${encodeURIComponent(currentOrigin)}` : ""}`;
    const popup = window.open(startUrl, "jewelflow-google-prefill", "width=520,height=720,resizable=yes,scrollbars=yes");
    if (!popup) {
      toast.error("Popup blocked. Please allow popups for Google autofill.");
      return;
    }
    googlePrefillPopupRef.current = popup;
  };

  const resendSignupOtp = async () => {
    if (otpCooldown > 0) return;
    setLoading(true);
    try {
      await api.post("/local-security/setup/request-otp", {
        email: signup.registered_email,
        mobile_number: signup.mobile_number,
        gst_number: signup.gst_number,
      });
      setOtpCooldown(60);
      toast.success("OTP resent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const continueToVaultStep = (e) => {
    e.preventDefault();
    if ((signup.otp || "").length !== 6) {
      toast.error("Enter valid 6-digit OTP");
      return;
    }
    setMode("signupVault");
  };

  const completeSignup = async (e) => {
    e.preventDefault();
    if (!signup.vault_password || !signup.confirm_vault_password) {
      toast.error("Enter vault password and confirmation");
      return;
    }
    if (signup.vault_password !== signup.confirm_vault_password) {
      toast.error("Vault passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const selectRes = await api.post("/local-security/businesses/select", { business_id: tenantId });
      localStorage.setItem("jewelflow_active_business_id", String(selectRes?.data?.active_business_id || tenantId));
      const res = await api.post("/local-security/setup", {
        first_name: signup.first_name,
        last_name: signup.last_name,
        business_name: signup.business_name,
        registered_email: signup.registered_email,
        mobile_number: signup.mobile_number,
        date_of_birth: new Date().toISOString().slice(0, 10),
        business_address: signup.business_address,
        city: signup.city,
        state: signup.state,
        gst_number: signup.gst_number,
        landline_number: signup.landline_number,
        password: signup.vault_password,
        owner_password: signup.login_password,
        otp: signup.otp,
        remember_device: true,
      });

      toast.success("Business created! Now set up your security questions.");
      setSecQSetup(Array.from({ length: 5 }, () => ({ question: "", answer: "" })));
      setMode("signupSecurityQuestions");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const submitStaffRegister = async (e) => {
    e.preventDefault();
    if (!staffRegister.full_name || !staffRegister.email || !staffRegister.password || !staffRegister.business_code) {
      toast.error("Please fill all staff registration fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        full_name: staffRegister.full_name,
        email: staffRegister.email,
        password: staffRegister.password,
        business_code: staffRegister.business_code,
      });
      toast.success("Staff user registered. Please sign in.");
      setMode("login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Staff registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{loginStyles}</style>
      <div className="mmj-page">
        <div className="mmj-bg" />
        <div className="mmj-blob mmj-blob-1" />
        <div className="mmj-blob mmj-blob-2" />
        <div className="mmj-blob mmj-blob-3" />

        <div className={`mmj-wrapper ${isSignupFlow ? "mmj-wrapper-wide" : "mmj-wrapper-compact"}`}>
          <div className="mmj-logo">
            <img src={`${process.env.PUBLIC_URL || "."}/jewelflow-logo-full.png`} alt="JewelFlow logo" className="mmj-logo-img" />
            <div className="mmj-sub">Crafted For Efficiency</div>
          </div>

          <div className={`mmj-card ${isSignupFlow ? "mmj-card-wide" : "mmj-card-compact"}`}>
            {mode === "login" && (
              <>
                <div className="mmj-top-row">
                  <div className="mmj-title" style={{ marginBottom: 0 }}>{t("login.sign_in")}</div>
                  <div
                    className="mmj-top-link"
                    onClick={() => setMode("register")}
                    title="Create a brand new business owner setup."
                    aria-label="Create a brand new business owner setup"
                  >
                    Create New Business
                  </div>
                </div>
                <form className="space-y-4" onSubmit={loginNow}>
                  <div className="space-y-1"><Label htmlFor="login-email">{t("common.email")}</Label><Input id="login-email" name="email" autoComplete="email" value={form.email} onChange={handle("email")} /></div>
                  <div className="space-y-1"><Label htmlFor="login-password">{t("login.password")}</Label><PasswordInput id="login-password" name="password" autoComplete="current-password" value={form.password} onChange={handle("password")} /></div>
                  {newDeviceOtpOpen && (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50/80 p-3 space-y-2 text-xs text-slate-800">
                      <div className="font-semibold">{t("login.new_device_detected")}</div>
                      <div>An OTP was sent to your registered email. Enter it to authorise this device.</div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor="login-new-device-otp">OTP</Label>
                        <Input
                          id="login-new-device-otp"
                          name="new_device_otp"
                          value={newDeviceOtp}
                          onChange={(e) => setNewDeviceOtp(e.target.value)}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                        />
                      </div>
                    </div>
                  )}
                  {takeoverOpen && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50/80 p-3 space-y-2 text-xs text-slate-800">
                      <div className="font-semibold">Maximum active sessions reached</div>
                      <div>Select old device sessions to logout, then verify OTP to continue on this device.</div>
                      <div className="max-h-32 overflow-y-auto rounded border border-amber-200 bg-white/80 p-2 space-y-1">
                        {takeoverSessions.length === 0 ? (
                          <div className="text-[11px] text-muted-foreground">No replaceable sessions found.</div>
                        ) : (
                          takeoverSessions.map((s) => {
                            const sid = String(s.id || "");
                            const checked = takeoverSelected.includes(sid);
                            return (
                              <label key={sid} className="flex items-center gap-2">
                                <input
                                  name={`takeover_session_${sid}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    if (e.target.checked) setTakeoverSelected((prev) => [...prev, sid]);
                                    else setTakeoverSelected((prev) => prev.filter((x) => x !== sid));
                                  }}
                                />
                                <span>{String(s.device_id || "device")} â€¢ {String(s.last_seen_at || "")}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {takeoverOtpSent && (
                        <div className="space-y-1">
                          <Label className="text-xs" htmlFor="login-takeover-otp">OTP</Label>
                          <Input id="login-takeover-otp" name="takeover_otp" value={takeoverOtp} onChange={(e) => setTakeoverOtp(e.target.value)} />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="mmj-btn-outline" onClick={requestTakeoverOtp} disabled={loading}>
                          {takeoverOtpSent ? t("login.resend_otp") : t("login.send_otp")}
                        </Button>
                        <Button type="button" className="mmj-btn" onClick={confirmTakeover} disabled={loading}>
                          {t("login.continue_here")}
                        </Button>
                      </div>
                    </div>
                  )}
                  {deviceLimitOpen && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50/80 p-3 space-y-2 text-xs text-slate-800">
                      <div className="font-semibold">Maximum active devices reached</div>
                      <div>Select old devices to logout/remove, then verify OTP to continue on this device.</div>
                      <div className="max-h-32 overflow-y-auto rounded border border-amber-200 bg-white/80 p-2 space-y-1">
                        {deviceLimitDevices.length === 0 ? (
                          <div className="text-[11px] text-muted-foreground">No replaceable devices found.</div>
                        ) : (
                          deviceLimitDevices.map((d) => {
                            const did = String(d.id || "");
                            const checked = deviceLimitSelected.includes(did);
                            return (
                              <label key={did} className="flex items-center gap-2">
                                <input
                                  name={`device_limit_${did}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    if (e.target.checked) setDeviceLimitSelected((prev) => [...prev, did]);
                                    else setDeviceLimitSelected((prev) => prev.filter((x) => x !== did));
                                  }}
                                />
                                <span>{String(d.device_name || d.platform || "device")} â€¢ {String(d.last_seen_at || "")}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {deviceLimitOtpSent && (
                        <div className="space-y-1">
                          <Label className="text-xs" htmlFor="login-device-limit-otp">OTP</Label>
                          <Input id="login-device-limit-otp" name="device_limit_otp" value={deviceLimitOtp} onChange={(e) => setDeviceLimitOtp(e.target.value)} />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="mmj-btn-outline" onClick={requestDeviceLimitOtp} disabled={loading}>
                          {deviceLimitOtpSent ? t("login.resend_otp") : t("login.send_otp")}
                        </Button>
                        <Button type="button" className="mmj-btn" onClick={confirmDeviceLimit} disabled={loading}>
                          {t("login.continue_here")}
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button
                    className="mmj-btn"
                    disabled={loading}
                    title="Sign in to your existing JewelFlow account."
                  >
                    {loading ? t("login.please_wait") : t("login.sign_in")}
                  </Button>
                  <div className="flex justify-between mt-2">
                    <div
                      className="mmj-link"
                      onClick={() => setMode("forgot")}
                      title="Reset the password for an existing account."
                    >
                      {t("login.reset_password_title")}
                    </div>
                    <div
                      className="mmj-link"
                      onClick={() => setMode("staffRegister")}
                      title="Join an existing business as staff using the business code."
                    >
                      {t("login.join_business")}
                    </div>
                  </div>
                </form>
                <div style={{ display: "flex", justifyContent: "center", marginTop: 18, position: "relative" }} ref={businessPickerRef}>
                  <button
                    type="button"
                    className="mmj-biz-pill"
                    onClick={() => setBusinessPickerOpen((v) => !v)}
                  >
                    <span className="mmj-biz-pill-name">{activeBusinessName || "JewelFlow"}</span>
                    <span style={{ fontSize: 9, opacity: 0.65, lineHeight: 1, flexShrink: 0 }}>&#9660;</span>
                  </button>

                  {businessPickerOpen && (
                    <div className="mmj-biz-picker">
                      <div className="mmj-biz-picker-head">Local Businesses</div>
                      {businesses.length === 0 && (
                        <div style={{ padding: "8px 14px 12px", fontSize: 12, color: "#6b7280" }}>
                          No businesses found
                        </div>
                      )}
                      {businesses.map((b) => {
                        const isActive = b.business_id === activeBusinessId;
                        const isLegacy = b.label === "legacy_clone" || b.label === "incomplete";
                        return (
                          <button
                            key={b.business_id}
                            type="button"
                            className={`mmj-biz-picker-item${isActive ? " active" : ""}${isLegacy ? " legacy" : ""}`}
                            onClick={() => switchBusiness(b.business_id)}
                          >
                            <div className="mmj-biz-picker-item-name">
                              <span style={{ flex: 1, textAlign: "left" }}>{b.business_name}</span>
                              {b.label && (
                                <span className={`mmj-biz-label ${b.label}`}>
                                  {b.label === "canonical" ? "canonical" : b.label === "legacy_clone" ? "legacy clone" : "incomplete"}
                                </span>
                              )}
                              {isActive && (
                                <span style={{ fontSize: 9, color: "#047857", fontWeight: 700, flexShrink: 0 }}>
                                  ACTIVE
                                </span>
                              )}
                            </div>
                            <div className="mmj-biz-picker-item-meta">
                              {b.business_id}
                              {b.tenant_id ? ` · ${b.tenant_id.slice(0, 8)}…` : " · no tenant ID"}
                              {b.registered_email ? ` · ${b.registered_email}` : ""}
                            </div>
                            {b.business_code && (
                              <div className="mmj-biz-picker-item-code">{b.business_code}</div>
                            )}
                            {isLegacy && b.warning && (
                              <div className="mmj-biz-picker-item-warning">⚠ {b.warning}</div>
                            )}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className="mmj-biz-picker-add"
                        onClick={() => {
                          setBusinessPickerOpen(false);
                          resetAddExistingState();
                          setAddExistingOpen(true);
                        }}
                      >
                        <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 400 }}>+</span>
                        Add Existing Business
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {mode === "businessReset" && (
              <>
                {resetFlow.executing ? (
                  <div className="space-y-6 text-center">
                    <div className="mmj-title" style={{ fontSize: 22 }}>One moment...</div>
                    <div className="text-sm text-muted-foreground">{resetFlow.progressLabel || "Working..."}</div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-emerald-600 transition-all duration-700"
                        style={{ width: `${resetFlow.progress}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">{resetFlow.progress}%</div>
                  </div>
                ) : resetFlow.step === 0 ? (
                  <>
                    <div className="mmj-title" style={{ fontSize: 22 }}>Emergency Business Reset</div>
                    <div className="space-y-4">
                      <div className="rounded-xl border border-border/70 bg-white/60 p-4 space-y-2">
                        <div className="text-sm font-semibold text-foreground">{resetFlow.businessName || "Business"}</div>
                        <div className="text-xs text-muted-foreground">Backup method: {resetFlow.backupMethod || "Not configured"}</div>
                        <div className="text-xs text-muted-foreground">
                          Last backup:{" "}
                          {resetFlow.lastBackupAt
                            ? (() => {
                                const days = resetFlow.backupAgeDays;
                                if (days === 0) return "Less than a day ago";
                                if (days === 1) return "1 day ago";
                                return `${days} days ago`;
                              })()
                            : "No backup found"}
                        </div>
                        {resetFlow.backupAgeDays !== null && resetFlow.backupAgeDays > 3 && (
                          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 mt-2">
                            Your most recent backup is {resetFlow.backupAgeDays === 1 ? "1 day" : `${resetFlow.backupAgeDays} days`} old.
                            Recent changes may not exist in backup copies.
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        This will reset the business workspace on this device. All local business data will be removed.
                        Your account and vault remain intact. You can restore from a backup after reset.
                      </p>
                      <Button className="mmj-btn" onClick={() => setResetFlow((prev) => ({ ...prev, step: 1 }))} disabled={loading}>
                        Continue to Authorization
                      </Button>
                      <div className="mmj-link" onClick={() => setMode("login")}>Cancel — Back to Sign In</div>
                    </div>
                  </>
                ) : resetFlow.step === 1 ? (
                  <>
                    <div className="mmj-title" style={{ fontSize: 20 }}>Step 1 — Verify Identity</div>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <Label>Business Vault Password</Label>
                        <PasswordInput
                          value={resetFlow.vaultPassword}
                          onChange={(e) => setResetFlow((prev) => ({ ...prev, vaultPassword: e.target.value }))}
                          placeholder="Enter vault password"
                        />
                      </div>
                      <Button className="mmj-btn" onClick={resetStepVerifyVault} disabled={loading}>
                        {loading ? "Verifying..." : "Verify"}
                      </Button>
                      <div className="mmj-link" onClick={() => setResetFlow((prev) => ({ ...prev, step: 0 }))}>Back</div>
                    </div>
                  </>
                ) : resetFlow.step === 2 ? (
                  <>
                    <div className="mmj-title" style={{ fontSize: 20 }}>Step 2 — Security Question</div>
                    <div className="space-y-4">
                      <div className="rounded-xl border border-border/70 bg-white/60 p-3">
                        <p className="text-sm font-medium text-foreground">{resetFlow.recoveryQuestion}</p>
                      </div>
                      <div className="space-y-1">
                        <Label>Your Answer</Label>
                        <Input
                          value={resetFlow.recoveryAnswer}
                          onChange={(e) => setResetFlow((prev) => ({ ...prev, recoveryAnswer: e.target.value }))}
                          placeholder="Enter your answer"
                        />
                      </div>
                      <Button className="mmj-btn" onClick={resetStepVerifyRecovery} disabled={loading}>
                        {loading ? "Verifying..." : "Verify Answer"}
                      </Button>
                      <div className="mmj-link" onClick={() => setResetFlow((prev) => ({ ...prev, step: 1 }))}>Back</div>
                    </div>
                  </>
                ) : resetFlow.step === 3 ? (
                  <>
                    <div className="mmj-title" style={{ fontSize: 20 }}>Step 3 — One-Time Verification</div>
                    <div className="space-y-4">
                      {!resetFlow.otpSent ? (
                        <>
                          <p className="text-sm text-muted-foreground">
                            An OTP will be sent to your registered email address.
                          </p>
                          <Button className="mmj-btn" onClick={resetStepRequestOtp} disabled={loading}>
                            {loading ? "Sending..." : "Send OTP"}
                          </Button>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to your email.</p>
                          <div className="space-y-2">
                            <OtpInputs
                              value={resetFlow.otp}
                              onChange={(v) => setResetFlow((prev) => ({ ...prev, otp: v }))}
                            />
                            <div className="text-xs text-muted-foreground">
                              {(resetFlow.otpCooldown || 0) > 0
                                ? `Resend in ${resetFlow.otpCooldown}s`
                                : <button type="button" className="underline" onClick={resetStepRequestOtp} disabled={loading}>Resend OTP</button>}
                            </div>
                          </div>
                          <Button
                            className="mmj-btn"
                            onClick={() => {
                              if (!resetFlow.otp || resetFlow.otp.length < 6) { toast.error("Enter the 6-digit OTP"); return; }
                              setResetFlow((prev) => ({ ...prev, step: 4 }));
                            }}
                            disabled={loading}
                          >
                            Continue
                          </Button>
                        </>
                      )}
                      <div className="mmj-link" onClick={() => setResetFlow((prev) => ({ ...prev, step: resetFlow.hasRecoveryQuestion ? 2 : 1 }))}>Back</div>
                    </div>
                  </>
                ) : resetFlow.step === 4 ? (
                  <>
                    <div className="mmj-title" style={{ fontSize: 20 }}>Step 4 — Final Confirmation</div>
                    <div className="space-y-4">
                      {resetFlow.confirmInfo ? (
                        <div className="rounded border border-amber-400 bg-amber-50 p-3 space-y-1 text-sm">
                          <p className="font-semibold text-amber-800">Confirm the target workspace</p>
                          <p><span className="text-muted-foreground">Business: </span>{resetFlow.confirmInfo.business_name || resetFlow.confirmInfo.business_id}</p>
                          <p><span className="text-muted-foreground">Admin: </span>{resetFlow.confirmInfo.admin_email || "—"}</p>
                          <Button
                            type="button"
                            className="mmj-btn w-full mt-1"
                            disabled={loading}
                            onClick={() => {
                              const id = resetFlow.confirmInfo.business_id;
                              setResetFlow((prev) => ({ ...prev, confirmInfo: null }));
                              resetStepExecute(id);
                            }}
                          >
                            Confirm Reset This Business
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800 space-y-1">
                            <p className="font-semibold">This action resets the business workspace.</p>
                            <p>Type <strong>DELETE BUSINESS</strong> or the exact business name to confirm.</p>
                          </div>
                          <div className="space-y-1">
                            <Label>Confirmation</Label>
                            <Input
                              value={resetFlow.typedConfirmation}
                              onChange={(e) => setResetFlow((prev) => ({ ...prev, typedConfirmation: e.target.value }))}
                              placeholder="DELETE BUSINESS"
                            />
                          </div>
                          <Button
                            className="mmj-btn"
                            style={{ background: "#7f1d1d !important" }}
                            onClick={() => resetStepExecute()}
                            disabled={loading}
                          >
                            {loading ? "Processing..." : "Reset Business Workspace"}
                          </Button>
                        </>
                      )}
                      <div className="mmj-link" onClick={() => setResetFlow((prev) => ({ ...prev, step: 3 }))}>Back</div>
                    </div>
                  </>
                ) : null}
              </>
            )}

            {mode === "forgot" && (
              <>
                <div className="mmj-title" style={{ fontSize: 22 }}>{t("login.reset_password_title")}</div>
                <p style={{ fontSize: 13, color: "#065f46", marginBottom: 16, marginTop: -12 }}>
                  Enter your account email. We'll verify your identity before sending an OTP.
                </p>
                <form className="space-y-4" onSubmit={requestForgotPassword}>
                  <div className="space-y-1">
                    <Label htmlFor="forgot-email">{t("common.email")}</Label>
                    <Input id="forgot-email" name="email" type="email" autoComplete="email" value={form.email} onChange={handle("email")} />
                  </div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Checking..." : "Continue"}</Button>
                  <div className="mmj-link" onClick={() => setMode("login")}>{t("login.back_to_sign_in")}</div>
                </form>
              </>
            )}

            {mode === "securityQuestion" && (
              <>
                <div className="mmj-step-bar">
                  <div className="mmj-step">
                    <div className="mmj-step-dot active">1</div>
                    <div className="mmj-step-label">Verify Identity</div>
                  </div>
                  <div className="mmj-step-connector" />
                  <div className="mmj-step">
                    <div className="mmj-step-dot">2</div>
                    <div className="mmj-step-label">Verify OTP</div>
                  </div>
                  <div className="mmj-step-connector" />
                  <div className="mmj-step">
                    <div className="mmj-step-dot">3</div>
                    <div className="mmj-step-label">Reset Password</div>
                  </div>
                </div>
                <div className="mmj-title" style={{ fontSize: 20, marginBottom: 14 }}>Security Verification</div>
                <form className="space-y-4" onSubmit={verifyRecoveryQuestion}>
                  <div className="mmj-recovery-card">
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#047857", textTransform: "uppercase", letterSpacing: ".05em" }}>Your Security Question</div>
                    <div className="mmj-recovery-question">{recoveryQuestion}</div>
                    <div className="space-y-1">
                      <Label htmlFor="recovery-answer">Your Answer</Label>
                      <Input
                        id="recovery-answer"
                        name="recovery_answer"
                        value={recoveryAnswer}
                        onChange={(e) => setRecoveryAnswer(e.target.value)}
                        placeholder="Type your answer"
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>Answer is case-insensitive. Spaces at the start and end are ignored.</div>
                  </div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Verifying..." : "Verify & Continue"}</Button>
                  <div className="mmj-link" onClick={() => { setMode("forgot"); setRecoveryAnswer(""); }}>Back</div>
                </form>
              </>
            )}

            {mode === "otp" && (
              <>
                <div className="mmj-step-bar">
                  <div className="mmj-step">
                    <div className="mmj-step-dot done">âœ“</div>
                    <div className="mmj-step-label">Verify Identity</div>
                  </div>
                  <div className="mmj-step-connector" />
                  <div className="mmj-step">
                    <div className="mmj-step-dot active">2</div>
                    <div className="mmj-step-label">Verify OTP</div>
                  </div>
                  <div className="mmj-step-connector" />
                  <div className="mmj-step">
                    <div className="mmj-step-dot active">3</div>
                    <div className="mmj-step-label">Reset Password</div>
                  </div>
                </div>
                <div className="mmj-title" style={{ fontSize: 20, marginBottom: 14 }}>{t("login.enter_otp")}</div>
                <form className="space-y-4" onSubmit={verifyOtpReset}>
                  <div className="space-y-1"><Label htmlFor="otp-email">{t("common.email")}</Label><Input id="otp-email" name="email" value={form.email} onChange={handle("email")} /></div>
                  <div className="space-y-1"><Label htmlFor="otp-code">OTP</Label><Input id="otp-code" name="otp" value={form.otp} onChange={handle("otp")} inputMode="numeric" autoComplete="one-time-code" /></div>
                  <div className="space-y-1"><Label htmlFor="otp-new-password">{t("login.new_password")}</Label><PasswordInput id="otp-new-password" name="new_password" autoComplete="new-password" value={form.newPassword} onChange={handle("newPassword")} /></div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? t("login.updating") : t("login.update_password")}</Button>
                </form>
              </>
            )}

            {mode === "register" && (
              <>
                <div className="mmj-top-row">
                  <div className="mmj-title" style={{ marginBottom: 0 }}>{t("login.create_business")}</div>
                  <div className="mmj-top-link" onClick={() => setMode("login")}>{t("login.back_to_sign_in")}</div>
                </div>
                <form className="space-y-4" onSubmit={submitSignupDetails}>
                  <div className="mmj-grid">
                    {(signup.google_prefill_full_name || signup.google_profile_image_url) && (
                      <div className="mmj-grid-full">
                        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-3 text-sm text-emerald-950">
                          {signup.google_profile_image_url ? (
                            <img
                              src={signup.google_profile_image_url}
                              alt="Google profile"
                              className="h-11 w-11 rounded-full border border-emerald-200 object-cover"
                              referrerPolicy="no-referrer"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : null}
                          <div>
                            <div className="font-medium">Google autofill loaded</div>
                            <div className="text-xs text-emerald-900/80">
                              This only prefills sign-up details. JewelFlow login stays email/password only.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1"><Label htmlFor="signup-first-name">First Name</Label><Input id="signup-first-name" name="first_name" value={signup.first_name} onChange={handleSignup("first_name")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-last-name">Last Name</Label><Input id="signup-last-name" name="last_name" value={signup.last_name} onChange={handleSignup("last_name")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-business-name">Business Name</Label><Input id="signup-business-name" name="business_name" value={signup.business_name} onChange={handleSignup("business_name")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-business-address">Business Address</Label><Input id="signup-business-address" name="business_address" value={signup.business_address} onChange={handleSignup("business_address")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-city">City</Label><Input id="signup-city" name="city" value={signup.city} onChange={handleSignup("city")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-state">State</Label><Input id="signup-state" name="state" value={signup.state} onChange={handleSignup("state")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-mobile-number">Business Mobile</Label><Input id="signup-mobile-number" name="mobile_number" value={signup.mobile_number} onChange={handleSignup("mobile_number")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-landline-number">Landline Number</Label><Input id="signup-landline-number" name="landline_number" value={signup.landline_number} onChange={handleSignup("landline_number")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-registered-email">Registered Email</Label><Input id="signup-registered-email" name="registered_email" type="email" value={signup.registered_email} onChange={handleSignup("registered_email")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-gst-number">GST Number</Label><Input id="signup-gst-number" name="gst_number" value={signup.gst_number} onChange={handleSignup("gst_number")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-login-password">Login Password</Label><PasswordInput id="signup-login-password" name="login_password" autoComplete="new-password" value={signup.login_password} onChange={handleSignup("login_password")} /></div>
                    <div className="space-y-1"><Label htmlFor="signup-confirm-login-password">Confirm Login Password</Label><PasswordInput id="signup-confirm-login-password" name="confirm_login_password" autoComplete="new-password" value={signup.confirm_login_password} onChange={handleSignup("confirm_login_password")} /></div>
                  </div>

                  <div className="text-xs text-emerald-900">Tenant ID preview: <b>{tenantId}</b></div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Submitting..." : t("login.complete_sign_up")}</Button>
                  <div className="mmj-sep"><span>Auto Fill</span></div>
                  <Button
                   type="button"
                   variant="outline"
                   className="w-full mmj-google-btn"
                   onClick={autofillFromGoogle}
                  >
                   <img
                     src={`${process.env.PUBLIC_URL || "."}/Googlelogo.png`}
                     alt="Google"
                     className="mmj-google-logo"
                     onError={(e) => { e.currentTarget.style.display = "none"; }}
                   />
                   Autofill from Google
                  </Button>

                </form>
              </>
            )}

            {mode === "staffRegister" && (
              <>
                <div className="mmj-top-row">
                  <div className="mmj-title" style={{ marginBottom: 0 }}>{t("login.join_business")}</div>
                  <div className="mmj-top-link" onClick={() => setMode("login")}>{t("login.back_to_sign_in")}</div>
                </div>
                <form className="space-y-4" onSubmit={submitStaffRegister}>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-3 text-sm text-emerald-950">
                    Join the existing business using your own credentials. Your admin must share the business code and approve your access.
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="staff-register-full-name">Full Name</Label>
                    <Input id="staff-register-full-name" name="full_name" value={staffRegister.full_name} onChange={handleStaffRegister("full_name")} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="staff-register-email">Email</Label>
                    <Input id="staff-register-email" name="email" type="email" value={staffRegister.email} onChange={handleStaffRegister("email")} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="staff-register-password">Password</Label>
                    <PasswordInput id="staff-register-password" name="password" autoComplete="new-password" value={staffRegister.password} onChange={handleStaffRegister("password")} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="staff-register-business-code">Business Code</Label>
                    <Input
                      id="staff-register-business-code"
                      name="business_code"
                      value={staffRegister.business_code}
                      onChange={handleStaffRegister("business_code")}
                      disabled={Boolean(staffRegisterLockedCode)}
                    />
                  </div>
                  <Button className="mmj-btn" disabled={loading}>
                    {loading ? "Registering..." : t("login.join_business")}
                  </Button>
                </form>
              </>
            )}

            {mode === "signupOtp" && (
              <>
                <div className="mmj-title">Verify OTP</div>
                <form className="space-y-4" onSubmit={continueToVaultStep}>
                  <Label htmlFor="signup-otp-0">Enter 6-digit OTP</Label>
                  <OtpInputs value={signup.otp} onChange={(v) => setSignup((s) => ({ ...s, otp: v }))} />
                  <div className="flex items-center justify-between text-sm text-emerald-900">
                    <span>{otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Didn't get OTP?"}</span>
                    <button type="button" className="underline disabled:no-underline" onClick={resendSignupOtp} disabled={otpCooldown > 0 || loading}>Resend OTP</button>
                  </div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Please wait..." : "Confirm OTP"}</Button>
                </form>
              </>
            )}

            {mode === "signupVault" && (
              <>
                <div className="mmj-title">Set Business Vault Password</div>
                <form className="space-y-4" onSubmit={completeSignup}>
                  <div className="space-y-1"><Label htmlFor="signup-vault-password">Vault Password</Label><PasswordInput id="signup-vault-password" name="vault_password" autoComplete="new-password" value={signup.vault_password} onChange={handleSignup("vault_password")} /></div>
                  <div className="space-y-1"><Label htmlFor="signup-confirm-vault-password">Confirm Vault Password</Label><PasswordInput id="signup-confirm-vault-password" name="confirm_vault_password" autoComplete="new-password" value={signup.confirm_vault_password} onChange={handleSignup("confirm_vault_password")} /></div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Creating workspace..." : "Next: Security Questions"}</Button>
                </form>
              </>
            )}

            {mode === "signupSecurityQuestions" && (
              <>
                <div className="mmj-top-row">
                  <div className="mmj-title" style={{ marginBottom: 0, fontSize: 22 }}>Security Questions</div>
                  <div style={{ fontSize: 12, color: "#047857", fontWeight: 600 }}>
                    {secQSetup.filter((q) => q.question && q.answer.trim()).length}/5
                  </div>
                </div>
                <div className="mmj-q-hint">Choose 5 questions only you would know. Used to verify your identity during password recovery.</div>
                <form className="space-y-4" onSubmit={submitSecurityQuestionsSetup}>
                  <div className="mmj-q-grid">
                    {secQSetup.map((item, idx) => {
                      const selectedInOthers = secQSetup
                        .filter((_, i) => i !== idx)
                        .map((q) => q.question)
                        .filter(Boolean);
                      const answered = item.question && item.answer.trim();
                      const hasCustomQuestion =
                        Boolean(item._customQ) ||
                        (item.question &&
                          item.question !== "__custom__" &&
                          !SECURITY_QUESTIONS_POOL.includes(item.question));
                      const selectValue = hasCustomQuestion
                        ? "__custom__"
                        : (item.question || "__placeholder__");
                      return (
                        <div key={idx} className={`mmj-q-card${answered ? " answered" : ""}`}>
                          <div className="mmj-q-num">Question {idx + 1}</div>
                          <Select
                            value={selectValue}
                            onValueChange={(value) =>
                              handleSecQChange(idx, "question", value === "__placeholder__" ? "" : value)
                            }
                            name={`sec_q_${idx}`}
                          >
                            <SelectTrigger className="h-10 rounded-[10px] border-[rgba(6,95,70,.3)] bg-white/90 text-[13px] text-slate-800 focus:ring-2 focus:ring-emerald-700/15 data-[placeholder]:text-slate-500">
                              <SelectValue placeholder="-- Select a question --" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__placeholder__">-- Select a question --</SelectItem>
                              {SECURITY_QUESTIONS_POOL.map((q) => (
                                <SelectItem key={q} value={q} disabled={selectedInOthers.includes(q)}>
                                  {q}
                                </SelectItem>
                              ))}
                              <SelectItem value="__custom__">+ Write your own question</SelectItem>
                            </SelectContent>
                          </Select>
                          {(item.question === "__custom__" || hasCustomQuestion) && (
                            <Input
                              placeholder="Type your custom question"
                              value={item._customQ || ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSecQSetup((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], _customQ: v, question: v || "__custom__" };
                                  return next;
                                });
                              }}
                              autoComplete="off"
                            />
                          )}
                          <Input
                            placeholder="Your answer"
                            value={item.answer}
                            onChange={(e) => handleSecQChange(idx, "answer", e.target.value)}
                            autoComplete="off"
                            name={`sec_a_${idx}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <Button className="mmj-btn" disabled={loading}>{loading ? "Saving..." : "Save Security Questions"}</Button>
                  <div className="mmj-link" onClick={() => { toast.info("Security questions skipped. You can set them up after logging in."); setMode("login"); }}>
                    Skip for now
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {switchConfirmBusiness && (
        <div className="mmj-biz-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSwitchConfirmBusiness(null); }}>
          <div className="mmj-biz-modal">
            <div style={{ fontSize: 18, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Switch to Legacy Folder?</div>
            <div style={{ fontSize: 13, color: "#1f2937", marginBottom: 6 }}>
              <strong>{switchConfirmBusiness.business_name}</strong>
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              Folder: <code style={{ background: "rgba(0,0,0,.06)", padding: "1px 4px", borderRadius: 3 }}>{switchConfirmBusiness.business_id}</code>
            </div>
            {switchConfirmBusiness.tenant_id ? (
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                Tenant: <code style={{ background: "rgba(0,0,0,.06)", padding: "1px 4px", borderRadius: 3 }}>{switchConfirmBusiness.tenant_id.slice(0, 16)}…</code>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "#b45309", marginBottom: 12 }}>No tenant ID — this folder was not registered through the standard setup flow.</div>
            )}
            <div style={{ fontSize: 12, color: "#92400e", background: "rgba(251,191,36,.12)", border: "1px solid rgba(217,119,6,.25)", borderRadius: 8, padding: "10px 12px", marginBottom: 18, lineHeight: 1.5 }}>
              ⚠ {switchConfirmBusiness.warning || "This folder may contain little or no data."}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "1px solid rgba(0,0,0,.12)", background: "transparent", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}
                onClick={() => setSwitchConfirmBusiness(null)}
              >
                Cancel
              </button>
              <button
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "none", background: "#b45309", fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff" }}
                onClick={() => { const b = switchConfirmBusiness; setSwitchConfirmBusiness(null); setSwitchVaultDialog(b); setSwitchVaultInput(""); }}
              >
                Switch Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {switchVaultDialog && (
        <div className="mmj-biz-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setSwitchVaultDialog(null); setSwitchVaultInput(""); } }}>
          <div className="mmj-biz-modal">
            <div style={{ fontSize: 18, fontWeight: 700, color: "#065f46", marginBottom: 8 }}>Switch Business</div>
            <div style={{ fontSize: 13, color: "#1f2937", marginBottom: 4 }}>
              <strong>{switchVaultDialog.business_name || switchVaultDialog.business_id}</strong>
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              Folder: <code style={{ background: "rgba(0,0,0,.06)", padding: "1px 4px", borderRadius: 3 }}>{switchVaultDialog.business_id}</code>
            </div>
            {switchVaultDialog.tenant_id && (
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                Tenant: <code style={{ background: "rgba(0,0,0,.06)", padding: "1px 4px", borderRadius: 3 }}>{switchVaultDialog.tenant_id.slice(0, 16)}…</code>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Vault Password
              </label>
              <PasswordInput
                value={switchVaultInput}
                onChange={(e) => setSwitchVaultInput(e.target.value)}
                placeholder="Enter vault password for this business"
                autoComplete="current-password"
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "1px solid rgba(0,0,0,.12)", background: "transparent", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}
                onClick={() => { setSwitchVaultDialog(null); setSwitchVaultInput(""); }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: "none", background: "#047857", fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff", opacity: (!switchVaultInput || loading) ? 0.55 : 1 }}
                onClick={() => { const bid = switchVaultDialog.business_id; const pwd = switchVaultInput; switchBusiness(bid, pwd); }}
                disabled={loading || !switchVaultInput}
              >
                {loading ? "Switching…" : "Switch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {addExistingOpen && (
        <div className="mmj-biz-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { closeAddExistingModal(); } }}>
          <div className="mmj-biz-modal" style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#1f2937", marginBottom: 6 }}>
              Add Existing Business
            </div>
            <div style={{ fontSize: 12, color: "#065f46", marginBottom: 20 }}>
              Restore or transfer an existing business to this device. Staff should still use Join Business with a business code.
            </div>

            <div style={{ background: "rgba(4,120,87,.06)", border: "1px solid rgba(4,120,87,.18)", borderRadius: 14, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#065f46", marginBottom: 6 }}>Supported in this step</div>
              <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6 }}>
                Select the secure transfer package created from your old primary laptop. This flow verifies the package, authenticates recovery identity, validates restore safety, and only then activates this device as Primary.
              </div>
            </div>

            {addExistingStep === "inspect" && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <Label htmlFor="add-existing-package" style={{ fontSize: 12 }}>Transfer Package</Label>
                  <Input
                    id="add-existing-package"
                    type="file"
                    accept=".jftransfer,.zip,application/zip"
                    style={{ marginTop: 6 }}
                    onChange={(e) => {
                      const nextFile = e.target.files?.[0] || null;
                      setAddExistingFile(nextFile);
                      setAddExistingInspectResult(null);
                      setAddExistingSessionId("");
                    }}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <Label htmlFor="add-existing-code" style={{ fontSize: 12 }}>Transfer Code</Label>
                  <Input
                    id="add-existing-code"
                    value={addExistingTransferCode}
                    onChange={(e) => setAddExistingTransferCode(e.target.value)}
                    placeholder="Required for verified restore"
                    autoComplete="off"
                  />
                </div>

                <Button
                  type="button"
                  className="mmj-btn"
                  onClick={inspectAddExistingPackage}
                  disabled={addExistingInspecting || !addExistingFile}
                  style={{ height: 42, fontSize: 14, marginBottom: 14 }}
                >
                  {addExistingInspecting ? "Inspecting Package..." : "Inspect Package"}
                </Button>
              </>
            )}

            {(addExistingInspectResult || addExistingRestoreResult || addExistingFinalizeResult) && (
              <div style={{ background: "rgba(4,120,87,.07)", border: "1px solid rgba(4,120,87,.22)", borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1f2937", marginBottom: 8 }}>
                  {(addExistingInspectResult?.preview?.business_name || addExistingRestoreResult?.preview?.business_name || addExistingFinalizeResult?.business_name || "Existing Business")}
                </div>
                <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.7 }}>
                  <div><strong>Owner Name:</strong> {addExistingInspectResult?.recovery_identity?.owner_full_name || "Not available"}</div>
                  <div><strong>Owner Email:</strong> {addExistingInspectResult?.recovery_identity?.owner_email || addExistingInspectResult?.preview?.owner_email || "Not available"}</div>
                  <div><strong>Owner Role:</strong> {addExistingInspectResult?.recovery_identity?.owner_role || "Not available"}</div>
                  <div><strong>Business Created:</strong> {addExistingInspectResult?.preview?.created_at || "Not available"}</div>
                  <div><strong>Package Created:</strong> {addExistingInspectResult?.created_at || addExistingInspectResult?.preview?.backup_created_at || "Not available"}</div>
                  <div><strong>Package Expiry:</strong> {addExistingInspectResult?.expires_at || "Not available"}</div>
                  <div><strong>Source Device:</strong> {addExistingInspectResult?.preview?.source_device_name || addExistingInspectResult?.source_primary?.device_name || "Not available"}</div>
                  <div><strong>Recovery Questions:</strong> {typeof addExistingInspectResult?.recovery_identity?.recovery_questions_count === "number" ? addExistingInspectResult.recovery_identity.recovery_questions_count : "Not available"}</div>
                </div>
                {Array.isArray(addExistingInspectResult?.validation_report) && addExistingInspectResult.validation_report.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#065f46", marginBottom: 6 }}>Validation Report</div>
                    <div style={{ display: "grid", gap: 6 }}>
                      {addExistingInspectResult.validation_report.map((item) => (
                        <div key={`inspect-${item.key}`} style={{ borderRadius: 10, border: `1px solid ${item.ok ? "rgba(4,120,87,.18)" : "rgba(220,38,38,.22)"}`, background: item.ok ? "rgba(4,120,87,.04)" : "rgba(220,38,38,.05)", padding: "8px 10px" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: item.ok ? "#065f46" : "#991b1b" }}>
                            {item.ok ? "Passed" : "Failed"} · {item.label}
                          </div>
                          <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2 }}>{item.detail}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 10, fontSize: 12, color: addExistingInspectResult?.verification?.transfer_code_verified ? "#065f46" : "#92400e" }}>
                  {addExistingInspectResult?.message || "Preview loaded. Enter the transfer code to complete package verification."}
                </div>
              </div>
            )}

            {addExistingStep === "verified" && (
              <div style={{ display: "grid", gap: 12, marginBottom: 14 }}>
                <div>
                  <Label htmlFor="add-existing-vault-password" style={{ fontSize: 12 }}>Business Vault Password</Label>
                  <Input
                    id="add-existing-vault-password"
                    type="password"
                    value={addExistingVaultPassword}
                    onChange={(e) => setAddExistingVaultPassword(e.target.value)}
                    placeholder="Enter the business vault password"
                    autoComplete="current-password"
                    style={{ marginTop: 6 }}
                  />
                </div>
                {addExistingRecoveryQuestion?.required && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1f2937", marginBottom: 6 }}>Security Question</div>
                    <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, marginBottom: 6 }}>
                      {addExistingRecoveryQuestion.question}
                    </div>
                    <Input
                      value={addExistingSecurityAnswer}
                      onChange={(e) => setAddExistingSecurityAnswer(e.target.value)}
                      placeholder="Enter your answer"
                      autoComplete="off"
                    />
                  </div>
                )}
                {addExistingInspectResult?.business_conflict && (
                  <div style={{ borderRadius: 10, border: "1px solid rgba(217,119,6,.28)", background: "rgba(251,191,36,.08)", padding: "10px 12px", fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
                    A local copy of this business already exists on this device. A safety backup will be required before replacement during final activation.
                  </div>
                )}
                <Button
                  type="button"
                  className="mmj-btn"
                  onClick={stageAddExistingRestore}
                  disabled={addExistingRestoring || !addExistingVaultPassword.trim() || (addExistingRecoveryQuestion?.required && !addExistingSecurityAnswer.trim())}
                  style={{ height: 42, fontSize: 14 }}
                >
                  {addExistingRestoring ? "Validating Restore..." : "Restore Business"}
                </Button>
              </div>
            )}

            {addExistingStep === "report" && addExistingRestoreResult && (
              <div style={{ display: "grid", gap: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1f2937" }}>Validation Report</div>
                <div style={{ fontSize: 12, color: "#065f46", lineHeight: 1.6 }}>
                  {addExistingRestoreResult?.message || "Restore validation completed."}
                </div>
                {Array.isArray(addExistingRestoreResult?.validation_report) && addExistingRestoreResult.validation_report.length > 0 && (
                  <div style={{ display: "grid", gap: 6 }}>
                    {addExistingRestoreResult.validation_report.map((item) => (
                      <div key={`restore-${item.key}`} style={{ borderRadius: 10, border: `1px solid ${item.ok ? "rgba(4,120,87,.18)" : "rgba(220,38,38,.22)"}`, background: item.ok ? "rgba(4,120,87,.04)" : "rgba(220,38,38,.05)", padding: "8px 10px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: item.ok ? "#065f46" : "#991b1b" }}>
                          {item.ok ? "Passed" : "Failed"} · {item.label}
                        </div>
                        <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2 }}>{item.detail}</div>
                      </div>
                    ))}
                  </div>
                )}
                {addExistingRestoreResult?.business_conflict && (
                  <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
                    <input
                      type="checkbox"
                      checked={addExistingReplaceLocalCopy}
                      onChange={(e) => setAddExistingReplaceLocalCopy(e.target.checked)}
                    />
                    <span>Replace the existing local copy after creating a safety backup.</span>
                  </label>
                )}
                <div style={{ borderRadius: 10, border: "1px solid rgba(4,120,87,.22)", background: "rgba(4,120,87,.06)", padding: "10px 12px", fontSize: 12, color: "#065f46", lineHeight: 1.6 }}>
                  Set this device as the new Primary Device? This activates the restored business here and blocks the old primary from continuing as the host device.
                </div>
                <Button
                  type="button"
                  className="mmj-btn"
                  onClick={finalizeAddExistingRestore}
                  disabled={addExistingFinalizing || Boolean(addExistingRestoreResult?.business_conflict) && !addExistingReplaceLocalCopy}
                  style={{ height: 42, fontSize: 14 }}
                >
                  {addExistingFinalizing ? "Activating Primary..." : "Activate This Device as Primary"}
                </Button>
              </div>
            )}

            {addExistingStep === "done" && addExistingFinalizeResult && (
              <div style={{ borderRadius: 12, border: "1px solid rgba(4,120,87,.22)", background: "rgba(4,120,87,.07)", padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#065f46" }}>
                  {addExistingFinalizeResult?.message || "Business moved successfully."}
                </div>
                <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.7, marginTop: 8 }}>
                  <div><strong>Active Business:</strong> {addExistingFinalizeResult?.active_business_id || "Not available"}</div>
                  <div>{addExistingFinalizeResult?.old_primary_notice || ""}</div>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={closeAddExistingModal}
              style={{ fontSize: 13, color: "#065f46", cursor: "pointer", textDecoration: "underline", display: "block", marginTop: 6 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
